import { memo, useEffect, useMemo } from "react";

import * as selectOptions from "@ui/components/DEPRECATED_CustomSelect/selectOptions";
import { Controller, useFormContext } from "@ui/components/Form";
import { GenderDropdown } from "@ui/components/GenderDropdown/GenderDropdown";
import type { OtpComponentProps } from "@ui/components/OtpComponent";
import { OtpComponent } from "@ui/components/OtpComponent";
import { useDateOfBirthFromAv } from "@ui/hooks/useDateOfBirthFromAv";
import { useNameFromAv } from "@ui/hooks/useNameFromAv";
import { useTranslations } from "@ui/providers/useTranslations";
import { getAge } from "@ui/utils/ageHelpers/ageHelpers";

import { useConfig } from "@jti/app-context/useConfig";
import { SupportingText } from "@jti/core-components/components/SupportingText";
import { applyNameOrder, isNonEmptyString } from "@jti/fe-common";

import { DateOfBirthFormProvider } from "../DateOfBirthFormProvider/DateOfBirthFormProvider";
import { TextInput } from "../Inputs";

import styles from "./PersonalDetails.module.scss";

const validateSlashesRegex = /^[^/\\]*$/;

export type PersonalDetailsProps = {
  fieldName?(name: string): string;
  isGenderOptionEnabled?: boolean;
  isDobChangeRestricted?: boolean;
  dobChangeRestrictedMessage?: string;
  isOnlyDobEditable?: boolean;
  isNameChangeAllowed?: boolean;
  isAutoOtpSendEnabled?: boolean;
  firstNameHelperText?: string;
  lastNameHelperText?: string;
  otpComponentVariant:
    | {
        type: "none";
      }
    | ({
        type: OtpComponentProps["variant"];
      } & Pick<
        OtpComponentProps,
        | "onSendCode"
        | "onCheckPhone"
        | "onSubmitOTP"
        | "attemptsNum"
        | "time"
        | "isCounterEnabled"
        | "isOTPenabled"
        | "isPhoneValidatedViaSms"
        | "isInputNum"
        | "isPhoneFieldFullWidth"
        | "defaultCountry"
        | "forceInternationalPhoneNumber"
        | "isSendCodeDisabled"
      >);
};

export const PersonalDetails = memo(
  ({
    fieldName = (name) => name,
    isGenderOptionEnabled = false,
    isDobChangeRestricted = false,
    isNameChangeAllowed = true,
    dobChangeRestrictedMessage,
    isOnlyDobEditable = false,
    isAutoOtpSendEnabled = false,
    firstNameHelperText: rawFirstNameHelperText,
    lastNameHelperText: rawLastNameHelperText,
    otpComponentVariant,
  }: PersonalDetailsProps) => {
    const { __ } = useTranslations();
    const config = useConfig();
    const { isSingleNameField, isReversedNameOrder } = config;
    const { watch, setError, clearErrors, control, getFieldState } = useFormContext();

    const dobPrefix = "dob";
    const dobDay = watch(`${dobPrefix}.dobDay`);
    const dobMonth = watch(`${dobPrefix}.dobMonth`);
    const dobYear = watch(`${dobPrefix}.dobYear`);
    const phoneValidationUuid = watch("phone_validation_uuid");

    const { isDobAutopopulated } = useDateOfBirthFromAv(dobPrefix);

    const { isNameAutopopulated } = useNameFromAv({
      firstnameField: fieldName("firstname"),
      lastnameField: fieldName("lastname"),
      isSingleNameField,
    });

    const lastNameFieldLabel = __(isSingleNameField ? "contact_info.name" : "contact_info.lastname");
    const lastNameFieldPlaceholder = __(
      isSingleNameField ? "contact_info.name_placeholder" : "contact_info.lastname_placeholder"
    );

    const yearsRange = useMemo(
      () => selectOptions.yearsArray(config.MIN_AGE_TO_REGISTER),
      [config.MIN_AGE_TO_REGISTER]
    );

    const firstNameHelperText = isNonEmptyString(rawFirstNameHelperText) ? rawFirstNameHelperText : undefined;
    const lastNameHelperText = isNonEmptyString(rawLastNameHelperText) ? rawLastNameHelperText : undefined;

    useEffect(() => {
      const { error } = getFieldState("date_of_birth");
      const day = getFieldState(`${dobPrefix}.dobDay`);
      const month = getFieldState(`${dobPrefix}.dobMonth`);
      const year = getFieldState(`${dobPrefix}.dobYear`);

      const isDobDirty = day?.isDirty || month?.isDirty || year?.isDirty;

      const isAgeLimitReached =
        getAge(new Date(dobYear?.value || dobYear, (dobMonth?.value || dobMonth) - 1, dobDay?.value || dobDay)) <
        (+config.MIN_AGE_TO_REGISTER || 18);

      if (isAgeLimitReached) {
        setError("date_of_birth", {
          type: "manual",
          message: __("form_validation.dob.age_limit"),
        });
      } else if (error?.type === "server" && !isDobDirty) {
        return;
      } else {
        clearErrors("date_of_birth");
      }
    }, [clearErrors, dobDay, dobMonth, dobYear, setError]);

    const firstNameField = !isSingleNameField ? (
      <Controller
        control={control}
        name={fieldName("firstname")}
        rules={{
          required: __("form_validation.field_required"),
          validate: (value) => {
            if (!validateSlashesRegex.test(value)) {
              return __("form_validation.validate_regex");
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            id="firstnameField"
            placeholder={__("contact_info.first_name_placeholder")}
            data-testid="firstnameField"
            label={__("contact_info.first_name")}
            isError={!!fieldState?.error}
            errorText={fieldState?.error?.message}
            helperText={firstNameHelperText}
            disabled={isNameAutopopulated || !isNameChangeAllowed}
            autoComplete="given-name"
            required
          />
        )}
      />
    ) : null;

    const lastNameField = (
      <Controller
        control={control}
        name={fieldName("lastname")}
        rules={{
          required: __("form_validation.field_required"),
          validate: (value) => {
            if (!validateSlashesRegex.test(value)) {
              return __("form_validation.validate_regex");
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            id="lastnameField"
            placeholder={lastNameFieldPlaceholder}
            data-testid="lastnameField"
            label={lastNameFieldLabel}
            isError={!!fieldState?.error}
            errorText={fieldState?.error?.message}
            helperText={lastNameHelperText}
            disabled={isNameAutopopulated || !isNameChangeAllowed}
            autoComplete="family-name"
            required
          />
        )}
      />
    );
    const [firstNameFieldOrdered, lastNameFieldOrdered] = applyNameOrder(
      firstNameField,
      lastNameField,
      isReversedNameOrder
    );

    return (
      <div className={styles.fields}>
        <div className={styles.fields}>
          {firstNameFieldOrdered}
          {lastNameFieldOrdered}
        </div>
        {isGenderOptionEnabled && (
          <Controller
            control={control}
            name={fieldName("gender")}
            rules={{ required: __("form_validation.field_required") }}
            render={({ field, fieldState }) => (
              <GenderDropdown
                {...field}
                label={__("contact_info.gender")}
                aria-label={__("contact_info.gender")}
                fieldName="gender"
                isDisabled={isOnlyDobEditable}
                isError={!!fieldState?.error}
                errorText={fieldState?.error?.message}
                isHelperTextVisible={!!fieldState?.error}
                required={true}
                showRequiredAsterisk={true}
              />
            )}
          />
        )}

        <div className={styles.personalFields}>
          <DateOfBirthFormProvider
            fieldsPrefix={dobPrefix}
            fieldName="date_of_birth"
            yearsRange={yearsRange}
            isDateChangeRestricted={isDobChangeRestricted || isDobAutopopulated}
            required
          />
          {isDobChangeRestricted && !!dobChangeRestrictedMessage && (
            <SupportingText variant="error" data-testid="dateChangeRestrictedWarning">
              {dobChangeRestrictedMessage}
            </SupportingText>
          )}
        </div>
        {/* @TODO - consider moving OtpComponent out of this component
        to avoid props drilling as it is nested here only in a single PersonalDetails usage */}
        {otpComponentVariant.type !== "none" && (
          <OtpComponent
            variant={otpComponentVariant.type}
            onSendCode={otpComponentVariant.onSendCode}
            onCheckPhone={otpComponentVariant.onCheckPhone}
            isShowBorder={true}
            time={otpComponentVariant.time}
            isCounterEnabled={otpComponentVariant.isCounterEnabled}
            isOTPenabled={otpComponentVariant.isOTPenabled}
            dataTestId="otpInput"
            attemptsNum={otpComponentVariant.attemptsNum}
            onSubmitOTP={otpComponentVariant.onSubmitOTP}
            isPhoneValidatedViaSms={otpComponentVariant.isPhoneValidatedViaSms}
            isInputNum={otpComponentVariant.isInputNum}
            autocompleteType="one-time-code"
            isPhoneValidated={Boolean(phoneValidationUuid)}
            isPhoneFieldFullWidth={otpComponentVariant.isPhoneFieldFullWidth}
            isAutoSendEnabled={isAutoOtpSendEnabled}
            defaultCountry={otpComponentVariant?.defaultCountry}
            forceInternationalPhoneNumber={otpComponentVariant?.forceInternationalPhoneNumber}
            isSendCodeDisabled={otpComponentVariant.isSendCodeDisabled}
          />
        )}
      </div>
    );
  }
);
